/**
 * AuthContext - Authentication and user management for Finding Your Voice
 *
 * Provides authentication state and methods throughout the app.
 * Supports both Firebase Auth for cloud sync and demo mode.
 *
 * The user profile syncs via the auth hub's per-app thin layer
 * (users/{uid}/apps/finding-your-voice, 'profile' section) so account
 * deletion reaches it. The app's own Firestore project is LEGACY: read once
 * per user (one-time import), never written for new state. localStorage is
 * the mirror page loads read from.
 */

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import {
  db,
  isDemoMode,
  currentAuthUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithGoogle as firebaseSignInWithGoogle,
  sendPasswordResetEmail,
  updateProfile,
} from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  hubSyncAvailable,
  hydrateAppState,
  resetHydrateCache,
  queuePatch,
  flushPatch,
  newerDoc,
  toPlainClient,
} from '../services/hubAppState';
import toast from 'react-hot-toast';

const PROFILE_KEY = 'fyv_profile';
const PROFILE_MIGRATED_KEY = 'fyv_hubMigratedProfile';

const readLocalProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeLocalProfile = (profile) => {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch (err) {
    console.warn('Could not persist profile locally:', err);
  }
};

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Demo user for development/offline mode
  const demoUser = {
    uid: 'demo-user-id',
    email: 'demo@findingyourvoice.app',
    displayName: 'Demo User',
    photoURL: null,
    emailVerified: true,
    isDemo: true,
  };

  /**
   * Load user profile — hub thin layer first (one shared read per login),
   * with a one-time import of the legacy own-project Firestore doc, merged
   * newest-wins against the local mirror.
   */
  const loadUserProfile = useCallback(async (userId) => {
    if (isDemoMode) return null;

    try {
      const hubState = hubSyncAvailable() ? await hydrateAppState() : null;

      let legacy = null;
      if (db && userId && !localStorage.getItem(PROFILE_MIGRATED_KEY)) {
        try {
          const profileSnap = await getDoc(doc(db, 'users', userId, 'profile', 'data'));
          legacy = profileSnap.exists() ? toPlainClient(profileSnap.data()) : null;
          localStorage.setItem(PROFILE_MIGRATED_KEY, '1');
        } catch {
          legacy = null; // no legacy access — retry the import next login
        }
      }

      const merged = newerDoc(newerDoc(hubState?.profile || null, legacy), readLocalProfile());
      if (merged) {
        writeLocalProfile(merged);
        if (hubSyncAvailable()) queuePatch({ profile: merged });
      }
      return merged;
    } catch (err) {
      console.error('Error loading user profile:', err);
      return readLocalProfile();
    }
  }, []);

  /**
   * Create or update the user profile — local mirror plus a debounced,
   * coalesced patch to the hub thin layer. The legacy own-project Firestore
   * is never written for new state.
   */
  const saveUserProfile = useCallback(async (userId, profileData) => {
    if (isDemoMode) return;

    try {
      const record = {
        ...(readLocalProfile() || {}),
        ...profileData,
        savedAt: new Date().toISOString(),
      };
      writeLocalProfile(record);
      if (hubSyncAvailable()) queuePatch({ profile: record });
    } catch (err) {
      console.error('Error saving user profile:', err);
    }
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = async (email, password) => {
    try {
      setError(null);

      if (isDemoMode) {
        setCurrentUser(demoUser);
        toast.success('Signed in (Demo Mode)');
        return demoUser;
      }

      const userCredential = await signInWithEmailAndPassword(email, password);
      toast.success('Welcome back!');
      return userCredential.user;
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message);

      if (err.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else if (err.code === 'auth/wrong-password') {
        toast.error('Incorrect password');
      } else if (err.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else if (err.code === 'auth/invalid-credential') {
        toast.error('Invalid email or password');
      } else {
        toast.error('Sign in failed');
      }

      throw err;
    }
  };

  /**
   * Sign in with Google
   */
  const signInWithGoogle = async () => {
    try {
      setError(null);

      if (isDemoMode) {
        setCurrentUser(demoUser);
        toast.success('Signed in with Google (Demo Mode)');
        return demoUser;
      }

      const userCredential = await firebaseSignInWithGoogle();
      toast.success(`Welcome, ${userCredential.user.displayName}!`);
      return userCredential.user;
    } catch (err) {
      console.error('Google sign in error:', err);
      setError(err.message);

      if (err.code === 'auth/popup-closed-by-user') {
        toast.error('Sign in cancelled');
      } else {
        toast.error('Google sign in failed');
      }

      throw err;
    }
  };

  /**
   * Create new account
   */
  const signUp = async (email, password, displayName) => {
    try {
      setError(null);

      if (isDemoMode) {
        toast.error('Sign up disabled in demo mode');
        throw new Error('Sign up disabled in demo mode');
      }

      const userCredential = await createUserWithEmailAndPassword(email, password);

      // Update display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Create user profile in Firestore
      await saveUserProfile(userCredential.user.uid, {
        displayName: displayName || null,
        email,
        createdAt: new Date().toISOString(),
      });

      toast.success('Account created successfully!');
      return userCredential.user;
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message);

      if (err.code === 'auth/email-already-in-use') {
        toast.error('Email already in use');
      } else if (err.code === 'auth/weak-password') {
        toast.error('Password too weak (minimum 6 characters)');
      } else {
        toast.error('Sign up failed');
      }

      throw err;
    }
  };

  /**
   * Sign out
   */
  const signOut = async () => {
    try {
      if (isDemoMode) {
        setCurrentUser(null);
        setUserProfile(null);
        toast.success('Signed out (Demo Mode)');
        return;
      }

      await flushPatch().catch(() => {}); // send any queued hub patch while the session lives
      await firebaseSignOut();
      toast.success('Signed out successfully');
    } catch (err) {
      console.error('Sign out error:', err);
      toast.error('Sign out failed');
      throw err;
    }
  };

  /**
   * Reset password
   */
  const resetPassword = async (email) => {
    try {
      setError(null);

      if (isDemoMode) {
        toast.success('Password reset email sent (Demo Mode)');
        return;
      }

      await sendPasswordResetEmail(email);
      toast.success('Password reset email sent');
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message);

      if (err.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else {
        toast.error('Password reset failed');
      }

      throw err;
    }
  };

  /**
   * Update user display name
   */
  const updateDisplayName = async (displayName) => {
    try {
      const user = currentAuthUser();
      if (isDemoMode || !user) {
        return;
      }

      await updateProfile(user, { displayName });
      await saveUserProfile(user.uid, { displayName });
      setCurrentUser({ ...currentUser, displayName });
      toast.success('Display name updated');
    } catch (err) {
      console.error('Update display name error:', err);
      toast.error('Failed to update display name');
      throw err;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    if (isDemoMode) {
      setCurrentUser(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(async (user) => {
      setCurrentUser(user);

      if (user) {
        const profile = await loadUserProfile(user.uid);
        setUserProfile(profile);

        // Update last active timestamp (non-blocking)
        saveUserProfile(user.uid, { lastActive: new Date().toISOString() }).catch(console.error);
      } else {
        setUserProfile(null);
        resetHydrateCache(); // next login fetches fresh hub state
      }

      setLoading(false);
      setError(null);
    });

    return unsubscribe;
  }, [loadUserProfile, saveUserProfile]);

  const value = {
    // State
    currentUser,
    userProfile,
    loading,
    error,
    isAuthenticated: !!currentUser,
    isDemoMode,

    // Methods
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,
    updateDisplayName,
    loadUserProfile,
    saveUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
